import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import { Loader2, Settings as SettingsIcon, Palette, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESETS = [
  { label: "Canary", days: 14 },
  { label: "Budgie", days: 18 },
  { label: "Lovebird", days: 21 },
  { label: "Cockatiel", days: 19 },
  { label: "Zebra Finch", days: 14 },
  { label: "Parrot", days: 26 },
];

const BG_COLORS = [
  { name: "Cream", value: "#F7F9F6" },
  { name: "Mist", value: "#EFF6FF" },
  { name: "Rose", value: "#FEF2F2" },
  { name: "Amber", value: "#FFFBEB" },
  { name: "Mint", value: "#F0FDF4" },
  { name: "Lilac", value: "#FAF5FF" },
  { name: "Sand", value: "#FAF7F2" },
  { name: "Slate", value: "#F1F5F9" },
];

const ACCENT_COLORS = [
  { name: "Olive", value: "#6B8E4F" },
  { name: "Blue", value: "#2563EB" },
  { name: "Red", value: "#DC2626" },
  { name: "Amber", value: "#D97706" },
  { name: "Purple", value: "#7C3AED" },
  { name: "Teal", value: "#0D9488" },
  { name: "Rose", value: "#E11D48" },
  { name: "Slate", value: "#334155" },
];

export default function GroupSettingsDialog({ open, onOpenChange }) {
  const { activeGroup, refresh, createGroup, switchGroup, deleteGroup } = useGroups();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [days, setDays] = useState(21);
  const [bg, setBg] = useState("#F7F9F6");
  const [accent, setAccent] = useState("#6B8E4F");
  const [saving, setSaving] = useState(false);
  const [newSeasonOpen, setNewSeasonOpen] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (activeGroup && open) {
      setName(activeGroup.name || "");
      setDays(activeGroup.incubation_days || 21);
      setBg(activeGroup.background_color || "#F7F9F6");
      setAccent(activeGroup.accent_color || "#6B8E4F");
      setNewSeasonOpen(false);
      setNewSeasonName("");
    }
  }, [activeGroup, open]);

  if (!activeGroup) return null;
  const isOwner = activeGroup.is_owner;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/groups/${activeGroup.id}/settings`, {
        name: name.trim(),
        incubation_days: Number(days),
        background_color: bg,
        accent_color: accent,
      });
      await refresh();
      toast.success("Saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onStartNewSeason = async () => {
    if (!newSeasonName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/groups/${activeGroup.id}/duplicate`, {
        name: newSeasonName.trim(),
      });
      await refresh();
      switchGroup(data.id);
      toast.success(`Started: ${data.name}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start new season");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    const groupName = activeGroup.name;
    try {
      const ok = await deleteGroup(activeGroup.id);
      if (ok) {
        toast.success(`Deleted: ${groupName}`);
        setDeleteConfirmOpen(false);
        onOpenChange(false);
      } else {
        toast.error("Delete failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-settings-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-olive" /> {t("settings.title")}
          </DialogTitle>
          <DialogDescription>
            {isOwner ? t("settings.sub_owner") : t("settings.sub_member")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <Label htmlFor="grp-name">{t("settings.name")}</Label>
            <Input
              id="grp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isOwner}
              className="mt-1.5"
              data-testid="settings-name-input"
            />
          </div>

          <div>
            <Label className="flex items-center justify-between">
              <span>{t("settings.incubation")}</span>
              <span className="font-mono text-olive font-semibold">{days} {t("reminders.days_unit")}</span>
            </Label>
            <input
              id="incubation"
              type="range"
              min="10"
              max="60"
              step="1"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              disabled={!isOwner}
              className="w-full mt-2 accent-olive"
              data-testid="settings-incubation-range"
            />
            <div className="mt-3 flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setDays(p.days)}
                  disabled={!isOwner}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    days === p.days
                      ? "bg-olive text-white border-olive"
                      : "bg-cream-2 text-espresso/70 border-app-border hover:bg-app-border"
                  } disabled:opacity-50`}
                  data-testid={`preset-${p.label.toLowerCase()}`}
                >
                  {p.label} <span className="font-mono opacity-70">· {p.days}d</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-olive" /> {t("settings.appearance")}
            </Label>
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-espresso/50 font-semibold mb-1">{t("settings.bg_color")}</div>
              <div className="flex flex-wrap gap-2" data-testid="bg-color-palette">
                {BG_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setBg(c.value)}
                    disabled={!isOwner}
                    className={`w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110 ${bg === c.value ? "border-espresso" : "border-app-border"}`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                    data-testid={`bg-${c.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-espresso/50 font-semibold mb-1">{t("settings.accent_color")}</div>
              <div className="flex flex-wrap gap-2" data-testid="accent-color-palette">
                {ACCENT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setAccent(c.value)}
                    disabled={!isOwner}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${accent === c.value ? "border-espresso" : "border-app-border"}`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                    data-testid={`accent-${c.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-xs text-espresso/60 mt-2">{t("settings.use_hint")}</p>
          </div>

          <div className="bg-cream-2 rounded-lg p-3 text-xs">
            <div className="text-[10px] uppercase tracking-widest text-olive font-bold mb-1">{t("group.invite_code")}</div>
            <div className="font-mono tracking-widest text-espresso font-bold text-sm">
              {activeGroup.invite_code}
            </div>
          </div>

          {isOwner && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <CalendarPlus className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-espresso">{t("settings.new_season")}</div>
                  <p className="text-xs text-espresso/70 mt-0.5">{t("settings.new_season_sub")}</p>
                  {!newSeasonOpen ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setNewSeasonOpen(true)}
                      className="mt-2"
                      data-testid="start-new-season"
                    >
                      <CalendarPlus className="w-3.5 h-3.5 mr-1" />
                      {t("settings.new_season_btn")}
                    </Button>
                  ) : (
                    <div className="mt-2 space-y-2">
                      <Label htmlFor="new-season-name" className="text-xs">{t("settings.new_season_label")}</Label>
                      <Input
                        id="new-season-name"
                        value={newSeasonName}
                        onChange={(e) => setNewSeasonName(e.target.value)}
                        placeholder={t("settings.new_season_placeholder")}
                        data-testid="new-season-name-input"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setNewSeasonOpen(false); setNewSeasonName(""); }}
                          disabled={creating}
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={onStartNewSeason}
                          disabled={creating || !newSeasonName.trim()}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="confirm-new-season"
                        >
                          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.new_season_btn")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isOwner && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="danger-zone">
              <div className="flex items-start gap-2">
                <Trash2 className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-red-900">{t("settings.danger_zone")}</div>
                  <div className="text-xs font-semibold text-espresso mt-0.5">{t("settings.delete_group")}</div>
                  <p className="text-xs text-espresso/70 mt-0.5">{t("settings.delete_sub")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="mt-2 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                    data-testid="delete-group-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    {t("settings.delete_btn")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          <Button
            onClick={onSave}
            disabled={!isOwner || saving || !name.trim()}
            className="bg-olive hover:bg-olive-hover text-white"
            data-testid="save-group-settings"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(v) => !deleting && setDeleteConfirmOpen(v)}>
        <AlertDialogContent data-testid="delete-group-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-900 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {t("settings.delete_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings.delete_confirm_body").replace("{name}", activeGroup?.name || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-group-no">
              {t("settings.delete_no")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-group-yes"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("settings.delete_yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
